import qs from 'query-string';
import { NAVER_APP_DOWNLOAD_LINK, PAY_APP_DOWNLOAD_LINK, APP_SCHEME, PACKAGE_NAME } from '../common/constant.mjs';
import { generateUserAgentValues } from './generateUserAgentValues.mjs';

/**
 * 네이버앱 다운로드 주소를 리턴합니다. userAgent 로 ios, Android를 분기합니다.
 * @returns 네이버앱 다운로드 주소
 */
function getNaverAppDownloadPage(userAgent) {
  const {
    isAndroid,
    isIOS
  } = generateUserAgentValues(userAgent);
  if (isAndroid) {
    return NAVER_APP_DOWNLOAD_LINK.ANDROID;
  }
  if (isIOS) {
    return NAVER_APP_DOWNLOAD_LINK.IOS;
  }
  return null;
}

/**
 * 페이앱 다운로드 주소를 리턴합니다. userAgent 로 ios, Android를 분기합니다.
 * @returns 페이앱 다운로드 주소
 */
function getPayAppDownloadPage(userAgent) {
  const {
    isAndroid,
    isIOS
  } = generateUserAgentValues(userAgent);
  if (isAndroid) {
    return PAY_APP_DOWNLOAD_LINK.ANDROID;
  }
  if (isIOS) {
    return PAY_APP_DOWNLOAD_LINK.IOS;
  }
  return null;
}

/**
 * userAgent 로 네이버앱/페이앱의 다운로드 페이지를 리턴합니다.
 */
function getAppDownloadPage(userAgent) {
  const {
    isNaverApp,
    isPayApp
  } = generateUserAgentValues(userAgent);
  if (isNaverApp) {
    return getNaverAppDownloadPage(userAgent);
  }
  if (isPayApp) {
    return getPayAppDownloadPage(userAgent);
  }
  return null;
}

/**
 * 안드로이드 바로가기 생성을 만들 수 있는 주소를 리턴합니다.
 * @param object: shortcutUrl, iconUrl, title을 인수로 받습니다.
 * @returns 바로가기 생성에 필요한 주소
 */
function getAndroidShortcutUrl(_ref) {
  let {
    shortcutUrl,
    iconUrl,
    title
  } = _ref;
  return `intent://addshortcut?url=${encodeURIComponent(shortcutUrl)}&icon=${encodeURIComponent(iconUrl)}&title=${encodeURIComponent(title)}&serviceCode=check&version=7#Intent;scheme=${APP_SCHEME.NAVER};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=${PACKAGE_NAME.NAVER};end`;
}
/**
 * 현재 사용자 아이디를 기반으로 페이앱을 실행하거나 다운로드 하도록 페이지 이동이 일어납니다.
 * idNo 파라미터가 전달되지 않은 경우 앱스킴 호출된 웹의 idNo와 페이앱의 idNo 검증 없이 페이앱만 실행합니다.
 * @param params {isAndroid: true, menuName: 메뉴명 } | { isIOS: true, menuName: 메뉴명, subMenuName: 서브메뉴명}
 * @deprecated runOrDownloadPayAppStrictly 를 대신 써주세요.
 * @see https://pages.oss.navercorp.com/techFin/payapp-docs/naverpay/guide/appscheme/
 */
function runOrDownloadPayApp(params) {
  const {
    menuName,
    subMenuName,
    url,
    idNo
  } = params;
  const queryParams = qs.stringify({
    menuName,
    subMenuName,
    idNo,
    url
  });
  if ('isAndroid' in params && params.isAndroid) {
    window.location.href = `intent://showservicemenu?${queryParams}#Intent;scheme=${APP_SCHEME.PAY};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=${PACKAGE_NAME.PAY};end`;
  } else if ('isIOS' in params && params.isIOS) {
    // iOS에서는 브라우저에 상관없이 유니버셜 링크를 사용가능
    // apple-app-site-association 에 브릿지 페이지를 등록하고, 앱 설치시 앱쪽에서 처리(url이동하지않고 os단에서 처리), 미설치시 브릿지 페이지로 이동하게 하여 마켓으로 리다이랙트 처리

    // 참고
    // safari: 설치되지 않은 앱의 스킴을 호출하면, "유효하지 않은 주소입니다" 에러가 노출(ios9 이하는 반응없음)
    // naverApp, chrome: 설치되지 않은 앱의 스킴을 호출하면 반응이 없음
    const urlScheme = `${APP_SCHEME.PAY}://showservicemenu?${queryParams}`;
    window.location.href = `https://app.pay.naver.com/bridge/payapp?urlScheme=${encodeURIComponent(urlScheme)}`;
  }
}
function runOrDownloadPayAppStrictly(params, deviceType) {
  const queryParams = qs.stringify(params);
  if (deviceType === 'android') {
    window.location.href = `intent://showservicemenu?${queryParams}#Intent;scheme=${APP_SCHEME.PAY};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=${PACKAGE_NAME.PAY};end`;
  }
  if (deviceType === 'ios') {
    // iOS에서는 브라우저에 상관없이 유니버셜 링크를 사용가능
    // apple-app-site-association 에 브릿지 페이지를 등록하고, 앱 설치시 앱쪽에서 처리(url이동하지않고 os단에서 처리), 미설치시 브릿지 페이지로 이동하게 하여 마켓으로 리다이랙트 처리

    // 참고
    // safari: 설치되지 않은 앱의 스킴을 호출하면, "유효하지 않은 주소입니다" 에러가 노출(ios9 이하는 반응없음)
    // naverApp, chrome: 설치되지 않은 앱의 스킴을 호출하면 반응이 없음
    const urlScheme = `${APP_SCHEME.PAY}://showservicemenu?${queryParams}`;
    window.location.href = `https://app.pay.naver.com/bridge/payapp?urlScheme=${encodeURIComponent(urlScheme)}`;
  } else {
    throw new Error('deviceType must be ios or android');
  }
}
const getPayAppShowServiceMenuURL = params => {
  const queryParams = qs.stringify(params);
  return `naverpayapp://showservicemenu${queryParams}`;
};
const stringifyUrl = _ref2 => {
  let {
    url,
    query
  } = _ref2;
  return `${url}?${qs.stringify(query, {
    encode: true
  })}`;
};

/**
 * 네이버 페이앱으로 이동하는 URL 반환
 *
 * - 딥링크/유니버셜 링크/앱링크란 @see https://oss.fin.navercorp.com/air-fe/guides/issues/34
 * - 네이버페이앱 브릿지 페이지 연결 가이드 @see https://oss.fin.navercorp.com/FinancialFE/plan/wiki/%EB%84%A4%EC%9D%B4%EB%B2%84%ED%8E%98%EC%9D%B4%EC%95%B1-%EB%B8%8C%EB%A6%BF%EC%A7%80-%ED%8E%98%EC%9D%B4%EC%A7%80-%EC%97%B0%EA%B2%B0-%EA%B0%80%EC%9D%B4%EB%93%9C
 * - 네이버페이 앱스킴 연동 @see https://pages.oss.navercorp.com/techFin/payapp-docs/naverpay/guide/appscheme/
 */
const getPayAppUrl = props => {
  const {
    shouldInAppBrowserOpen,
    deviceType,
    phase = 'real'
  } = props;
  /** ios는 브릿지에서 phase 지원 X, android는 phase 지원 O */
  const payOriginUrl = `new-${phase === 'real' ? '' : `${phase}-`}m.pay.naver.com`;
  const originUrl = deviceType === 'ios' ? 'app.pay.naver.com' : payOriginUrl;
  const bridgeUrl = `https://${originUrl}/bridge/payapp${deviceType === 'ios' ? '' : '-android'}`;
  return stringifyUrl({
    url: bridgeUrl,
    query: {
      ...(deviceType === 'ios' && {
        urlScheme: stringifyUrl({
          url: `${APP_SCHEME.PAY}://${shouldInAppBrowserOpen ? 'inappbrowser' : 'showservicemenu'}`,
          query: {
            ...(shouldInAppBrowserOpen ? {
              url: props.landingUrl
            } : {
              ...props.serviceMenuOption
            })
          }
        })
      }),
      /**
       * showservicemenu로 접근할 때 `intent://` scheme이 QR로 그리면 접근 불가능 @see https://oss.fin.navercorp.com/air-fe/guides/issues/34 (FAQ 4번)
       * bridge에서 기능을 제공해줘서 @see https://oss.fin.navercorp.com/point-benefit-fe/naverpay-web/blob/main/src/pages/bridge/payapp-android/index.tsx
       * `intent://showservicemenu?`로 전달할 파라미터를 bridge 파라미터로 전달해주면 접근 가능
       */
      ...(deviceType === 'android' && {
        inappbrowser: shouldInAppBrowserOpen,
        /** landingUrl이 없으면 이동되지 않는 이슈가 있어서 빈문자열 추가 */
        landingUrl: '',
        ...(shouldInAppBrowserOpen ? {
          landingUrl: props.landingUrl
        } : {
          ...props.serviceMenuOption,
          /**
           * 브릿지에서 url 파라미터는 사용되지 않고 대신 landingUrl이 사용됨
           * @see https://oss.fin.navercorp.com/point-benefit-fe/naverpay-web/blob/release/240821/src/pages/bridge/payapp-android/index.tsx#L13
           */
          ...('url' in props.serviceMenuOption && {
            landingUrl: props.serviceMenuOption.url || ''
          })
        })
      })
    }
  });
};
const getNaverAppUrl = _ref3 => {
  let {
    landingUrl,
    deviceType,
    from
  } = _ref3;
  switch (deviceType) {
    case 'ios':
      return stringifyUrl({
        url: 'https://naverapp.m.naver.com',
        query: {
          urlScheme: stringifyUrl({
            url: `${APP_SCHEME.NAVER}://inappbrowser`,
            query: {
              url: landingUrl
            }
          })
        }
      });
    case 'android':
      return stringifyUrl({
        url: 'https://app.pay.naver.com/bridge/naverapp-android',
        query: {
          landingUrl,
          /**
           * @see https://oss.fin.navercorp.com/common-fe/pie/issues/2275
           * @see https://oss.fin.navercorp.com/point-benefit-fe/naverpay-web/blob/main/src/pages/bridge/naverapp-android/index.tsx#L33-L35
           */
          ...(from && {
            from
          })
        }
      });
  }
};

export { getAndroidShortcutUrl, getAppDownloadPage, getNaverAppDownloadPage, getNaverAppUrl, getPayAppDownloadPage, getPayAppShowServiceMenuURL, getPayAppUrl, runOrDownloadPayApp, runOrDownloadPayAppStrictly };
